import { TREE_LOG_BLOCK_IDS, TREE_PLANK_BLOCK_IDS } from './game-data.js';
import { rand, clamp01 } from './utils/math-helpers.js';

export function createSoundSystem() {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  let masterVolume = 1;
  let noiseBuffer = null;
  const WOOD_SOUND_BLOCK_IDS = new Set([
    ...TREE_LOG_BLOCK_IDS,
    ...TREE_PLANK_BLOCK_IDS,
    11, 14, 15, 16, 17, 18, 19, 20
  ]);

  function isWoodSoundBlock(blockId) {
    return WOOD_SOUND_BLOCK_IDS.has(Number(blockId));
  }

  function resume() {
    if (audioCtx.state === 'suspended') audioCtx.resume();
  }

  function playTransient({ type = 'triangle', freq = 220, duration = 0.12, volume = 0.08, detune = 0, attack = 0.002 }) {
    const scaledVolume = clamp01(masterVolume) * Math.max(0, Number(volume) || 0);
    if (scaledVolume <= 0.0001) return;

    resume();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    const now = audioCtx.currentTime;
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    osc.detune.setValueAtTime(detune, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, scaledVolume), now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  function getNoiseBuffer() {
    if (noiseBuffer) return noiseBuffer;
    const length = Math.max(2048, Math.floor(audioCtx.sampleRate));
    const buffer = audioCtx.createBuffer(1, length, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2) - 1;
    noiseBuffer = buffer;
    return noiseBuffer;
  }

  function playNoiseBurst({
    duration = 0.08,
    volume = 0.045,
    attack = 0.001,
    hp = 900,
    lp = 9000,
    delay = 0
  } = {}) {
    const scaledVolume = clamp01(masterVolume) * Math.max(0, Number(volume) || 0);
    if (scaledVolume <= 0.0001) return;

    resume();
    const now = audioCtx.currentTime + Math.max(0, Number(delay) || 0);
    const src = audioCtx.createBufferSource();
    src.buffer = getNoiseBuffer();

    const hpFilter = audioCtx.createBiquadFilter();
    hpFilter.type = 'highpass';
    hpFilter.frequency.setValueAtTime(Math.max(30, Number(hp) || 900), now);

    const lpFilter = audioCtx.createBiquadFilter();
    lpFilter.type = 'lowpass';
    lpFilter.frequency.setValueAtTime(Math.max(150, Number(lp) || 9000), now);

    const gain = audioCtx.createGain();
    const safeAttack = Math.max(0.0005, Number(attack) || 0.001);
    const safeDuration = Math.max(0.02, Number(duration) || 0.08);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, scaledVolume), now + safeAttack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + safeDuration);

    src.connect(hpFilter);
    hpFilter.connect(lpFilter);
    lpFilter.connect(gain);
    gain.connect(audioCtx.destination);

    src.start(now, rand(0, 0.15));
    src.stop(now + safeDuration + 0.03);
  }

  function playDigLayer(baseFreq, baseType, spread = 40) {
    playTransient({ type: baseType, freq: baseFreq + rand(-spread, spread), duration: rand(0.06, 0.12), volume: rand(0.05, 0.09), detune: rand(-80, 80) });
    playTransient({ type: 'triangle', freq: baseFreq * rand(1.8, 2.6), duration: rand(0.03, 0.08), volume: rand(0.015, 0.03), detune: rand(-60, 60) });
  }

  const digVariants = {
    grass: [
      () => playDigLayer(210, 'triangle', 60),
      () => playDigLayer(170, 'sine', 40),
      () => playDigLayer(230, 'triangle', 30),
      () => playDigLayer(190, 'sawtooth', 25)
    ],
    wood: [
      () => playDigLayer(130, 'sawtooth', 25),
      () => playDigLayer(115, 'square', 20),
      () => playDigLayer(140, 'triangle', 18),
      () => playDigLayer(105, 'square', 25)
    ],
    leaves: [
      () => {
        playTransient({ type: 'triangle', freq: rand(280, 360), duration: 0.045, volume: 0.035 });
        playTransient({ type: 'sine', freq: rand(420, 520), duration: 0.05, volume: 0.02 });
      },
      () => {
        playTransient({ type: 'sine', freq: rand(260, 340), duration: 0.05, volume: 0.03 });
        playTransient({ type: 'triangle', freq: rand(380, 480), duration: 0.03, volume: 0.017 });
      },
      () => {
        playTransient({ type: 'triangle', freq: rand(250, 320), duration: 0.06, volume: 0.03 });
        playTransient({ type: 'square', freq: rand(360, 430), duration: 0.025, volume: 0.014 });
      }
    ],
    glass: [
      () => {
        playNoiseBurst({ duration: rand(0.03, 0.05), volume: 0.016, hp: 1500, lp: 9800 });
        playTransient({ type: 'triangle', freq: rand(920, 1240), duration: 0.04, volume: 0.034, attack: 0.001 });
        playTransient({ type: 'sine', freq: rand(1420, 1920), duration: 0.028, volume: 0.022, detune: rand(-120, 120), attack: 0.001 });
      },
      () => {
        playNoiseBurst({ duration: rand(0.03, 0.055), volume: 0.017, hp: 1700, lp: 11200 });
        playTransient({ type: 'sine', freq: rand(860, 1140), duration: 0.045, volume: 0.03, attack: 0.001 });
        playTransient({ type: 'triangle', freq: rand(1550, 2100), duration: 0.026, volume: 0.018, detune: rand(-140, 140), attack: 0.001 });
      },
      () => {
        playNoiseBurst({ duration: rand(0.025, 0.05), volume: 0.016, hp: 1400, lp: 9200 });
        playTransient({ type: 'triangle', freq: rand(980, 1320), duration: 0.034, volume: 0.033, attack: 0.001 });
        playTransient({ type: 'square', freq: rand(1680, 2260), duration: 0.02, volume: 0.014, detune: rand(-180, 180), attack: 0.001 });
      }
    ],
    stone: [
      () => playDigLayer(88, 'square', 18),
      () => playDigLayer(95, 'sawtooth', 14),
      () => playDigLayer(80, 'square', 10),
      () => playDigLayer(76, 'triangle', 16)
    ]
  };

  function playGlassBreak() {
    const base = rand(920, 1180);
    playNoiseBurst({ duration: 0.1, volume: 0.058, hp: 850, lp: 9800, attack: 0.0008 });
    playNoiseBurst({ duration: 0.065, volume: 0.041, hp: 1700, lp: 12000, delay: 0.018, attack: 0.0008 });
    playTransient({ type: 'square', freq: rand(1850, 2350), duration: 0.018, volume: 0.044, attack: 0.0007 });
    playTransient({ type: 'triangle', freq: base, duration: 0.05, volume: 0.052, attack: 0.0009 });
    setTimeout(() => playTransient({ type: 'sine', freq: base * rand(1.45, 1.8), duration: 0.04, volume: 0.045, detune: rand(-180, 180), attack: 0.001 }), 14);
    setTimeout(() => playTransient({ type: 'triangle', freq: base * rand(1.95, 2.35), duration: 0.032, volume: 0.032, detune: rand(-260, 260), attack: 0.001 }), 32);
    setTimeout(() => playTransient({ type: 'square', freq: base * rand(2.5, 3.05), duration: 0.022, volume: 0.023, detune: rand(-320, 320), attack: 0.001 }), 58);
  }

  const pickupVariants = [
    () => {
      const b = rand(620, 760);
      playTransient({ type: 'sine', freq: b, duration: 0.08, volume: 0.04 });
      setTimeout(() => playTransient({ type: 'triangle', freq: b * 1.35, duration: 0.1, volume: 0.045 }), 55);
    },
    () => {
      const b = rand(520, 640);
      playTransient({ type: 'triangle', freq: b, duration: 0.06, volume: 0.04 });
      setTimeout(() => playTransient({ type: 'sine', freq: b * 1.7, duration: 0.08, volume: 0.04 }), 40);
    },
    () => {
      const b = rand(680, 860);
      playTransient({ type: 'square', freq: b, duration: 0.05, volume: 0.03 });
      setTimeout(() => playTransient({ type: 'triangle', freq: b * 1.22, duration: 0.1, volume: 0.04 }), 30);
    },
    () => {
      const b = rand(560, 740);
      playTransient({ type: 'sine', freq: b, duration: 0.07, volume: 0.035 });
      setTimeout(() => playTransient({ type: 'sine', freq: b * 1.5, duration: 0.07, volume: 0.03 }), 65);
      setTimeout(() => playTransient({ type: 'triangle', freq: b * 1.85, duration: 0.08, volume: 0.03 }), 95);
    }
  ];

  const placeVariants = {
    grass: [
      () => {
        playTransient({ type: 'triangle', freq: rand(84, 110), duration: 0.1, volume: 0.06 });
        playTransient({ type: 'sine', freq: rand(140, 180), duration: 0.06, volume: 0.03, attack: 0.001 });
      },
      () => {
        playTransient({ type: 'sawtooth', freq: rand(72, 94), duration: 0.13, volume: 0.05 });
        playTransient({ type: 'triangle', freq: rand(120, 150), duration: 0.05, volume: 0.025 });
      },
      () => {
        playTransient({ type: 'triangle', freq: rand(92, 118), duration: 0.08, volume: 0.06 });
        playTransient({ type: 'square', freq: rand(150, 190), duration: 0.04, volume: 0.02 });
      }
    ],
    dirt: [
      () => {
        playTransient({ type: 'square', freq: rand(62, 82), duration: 0.11, volume: 0.065 });
        playTransient({ type: 'triangle', freq: rand(90, 120), duration: 0.06, volume: 0.03 });
      },
      () => {
        playTransient({ type: 'sawtooth', freq: rand(58, 74), duration: 0.14, volume: 0.05 });
        playTransient({ type: 'sine', freq: rand(96, 110), duration: 0.07, volume: 0.03 });
      },
      () => {
        playTransient({ type: 'triangle', freq: rand(68, 90), duration: 0.09, volume: 0.055 });
        playTransient({ type: 'triangle', freq: rand(112, 138), duration: 0.04, volume: 0.02 });
      }
    ],
    wood: [
      () => {
        playTransient({ type: 'square', freq: rand(118, 142), duration: 0.1, volume: 0.06 });
        playTransient({ type: 'triangle', freq: rand(180, 225), duration: 0.06, volume: 0.02 });
      },
      () => {
        playTransient({ type: 'sawtooth', freq: rand(104, 128), duration: 0.12, volume: 0.055 });
        playTransient({ type: 'triangle', freq: rand(165, 210), duration: 0.05, volume: 0.025 });
      },
      () => {
        playTransient({ type: 'square', freq: rand(126, 150), duration: 0.09, volume: 0.06 });
        playTransient({ type: 'sine', freq: rand(196, 240), duration: 0.04, volume: 0.02 });
      }
    ],
    leaves: [
      () => {
        playTransient({ type: 'triangle', freq: rand(190, 240), duration: 0.05, volume: 0.04 });
        playTransient({ type: 'sine', freq: rand(280, 350), duration: 0.05, volume: 0.02 });
      },
      () => {
        playTransient({ type: 'sine', freq: rand(210, 260), duration: 0.06, volume: 0.035 });
        playTransient({ type: 'triangle', freq: rand(320, 390), duration: 0.04, volume: 0.018 });
      },
      () => {
        playTransient({ type: 'triangle', freq: rand(180, 228), duration: 0.07, volume: 0.035 });
        playTransient({ type: 'sawtooth', freq: rand(260, 320), duration: 0.03, volume: 0.016 });
      }
    ],
    stone: [
      () => {
        playTransient({ type: 'square', freq: rand(68, 84), duration: 0.11, volume: 0.055 });
        playTransient({ type: 'triangle', freq: rand(100, 126), duration: 0.06, volume: 0.025 });
      },
      () => {
        playTransient({ type: 'sawtooth', freq: rand(72, 90), duration: 0.09, volume: 0.05 });
        playTransient({ type: 'square', freq: rand(104, 132), duration: 0.05, volume: 0.02 });
      },
      () => {
        playTransient({ type: 'square', freq: rand(64, 80), duration: 0.13, volume: 0.052 });
        playTransient({ type: 'triangle', freq: rand(92, 116), duration: 0.05, volume: 0.024 });
      }
    ],
    torch: [
      () => {
        playTransient({ type: 'triangle', freq: rand(220, 280), duration: 0.06, volume: 0.045 });
        playTransient({ type: 'sine', freq: rand(320, 390), duration: 0.05, volume: 0.02 });
      },
      () => {
        playTransient({ type: 'sine', freq: rand(240, 310), duration: 0.07, volume: 0.04 });
        playTransient({ type: 'triangle', freq: rand(340, 430), duration: 0.04, volume: 0.019 });
      }
    ]
  };

  function playStepBySurface(surfaceId, inWater = false, sprinting = false) {
    if (inWater) {
      playTransient({ type: 'sine', freq: rand(160, 210), duration: 0.07, volume: sprinting ? 0.045 : 0.035 });
      playTransient({ type: 'triangle', freq: rand(92, 128), duration: 0.06, volume: 0.025 });
      return;
    }

    const loudness = sprinting ? 1.15 : 1.0;
    if (isWoodSoundBlock(surfaceId)) {
      playTransient({ type: 'square', freq: rand(120, 150), duration: 0.06, volume: 0.038 * loudness });
      playTransient({ type: 'triangle', freq: rand(170, 220), duration: 0.04, volume: 0.018 * loudness });
      return;
    }

    if (surfaceId === 2 || surfaceId === 26 || surfaceId === 27 || surfaceId === 41) {
      playTransient({ type: 'square', freq: rand(70, 88), duration: 0.075, volume: 0.036 * loudness });
      playTransient({ type: 'triangle', freq: rand(100, 132), duration: 0.045, volume: 0.017 * loudness });
      return;
    }

    if (
      surfaceId === 5 || surfaceId === 8 || surfaceId === 9 || surfaceId === 10 || surfaceId === 13 ||
      surfaceId === 21 || surfaceId === 22 || surfaceId === 23 || surfaceId === 24 || surfaceId === 25 ||
      surfaceId === 28 || surfaceId === 29 || surfaceId === 30 || surfaceId === 31 ||
      surfaceId === 32 || surfaceId === 33 || surfaceId === 34 || surfaceId === 35 ||
      surfaceId === 36 || surfaceId === 37 || surfaceId === 38 || surfaceId === 39 ||
      surfaceId === 44 || surfaceId === 45 ||
      surfaceId === 40 || surfaceId === 42
    ) {
      playTransient({ type: 'square', freq: rand(78, 96), duration: 0.07, volume: 0.04 * loudness });
      playTransient({ type: 'triangle', freq: rand(105, 130), duration: 0.04, volume: 0.015 * loudness });
      return;
    }

    if (surfaceId === 4) {
      playTransient({ type: 'triangle', freq: rand(240, 320), duration: 0.05, volume: 0.028 * loudness });
      playTransient({ type: 'sine', freq: rand(350, 430), duration: 0.03, volume: 0.012 * loudness });
      return;
    }

    playTransient({ type: 'triangle', freq: rand(94, 128), duration: 0.065, volume: 0.035 * loudness });
    playTransient({ type: 'sine', freq: rand(132, 170), duration: 0.04, volume: 0.016 * loudness });
  }

  function isBurnDamageType(type = 'mob') {
    return type === 'lava' || type === 'fire';
  }

  function isProjectileDamageType(type = 'mob') {
    return type === 'skeleton_archer' || type === 'stray' || type === 'pillager';
  }

  // --- 岩漿 / 火焰灼燒（持續傷害的每 tick 音效）---
  function playBurnHurt(type = 'fire') {
    const lava = type === 'lava';
    // 粗糙灼燒噪音 — 低頻為主，聽起來像持續燒灼
    playNoiseBurst({
      duration: lava ? 0.14 : 0.1,
      volume: lava ? 0.045 : 0.032,
      hp: lava ? 400 : 600,
      lp: lava ? 2800 : 3600,
      attack: 0.001
    });
    // 痛苦悶聲
    playTransient({
      type: 'sawtooth',
      freq: lava ? rand(120, 180) : rand(160, 240),
      duration: lava ? 0.12 : 0.09,
      volume: lava ? 0.04 : 0.028,
      detune: rand(-200, 200)
    });
    // 灼傷尾音
    setTimeout(() => {
      playTransient({
        type: 'triangle',
        freq: lava ? rand(200, 320) : rand(280, 400),
        duration: lava ? 0.09 : 0.06,
        volume: lava ? 0.025 : 0.016,
        detune: rand(-250, 250)
      });
    }, lava ? 35 : 22);
  }

  // --- 怪物 / 箭矢 / 爆炸 / 環境打擊 ---
  function playMonsterHurt(type = 'mob') {
    if (type === 'void') return;

    // 溺水：水下泡泡 + 嗆水
    if (type === 'drown') {
      playTransient({ type: 'sine', freq: rand(160, 240), duration: 0.18, volume: 0.035 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(80, 110), duration: 0.2, volume: 0.03 }), 25);
      setTimeout(() => playNoiseBurst({ duration: 0.08, volume: 0.025, hp: 100, lp: 600 }), 60);
      return;
    }

    // 墜落：骨頭碎裂 + 碎石飛濺
    if (type === 'fall') {
      playTransient({ type: 'square', freq: rand(60, 85), duration: 0.15, volume: 0.07 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(130, 180), duration: 0.08, volume: 0.04 }), 20);
      setTimeout(() => playNoiseBurst({ duration: 0.07, volume: 0.03, hp: 400, lp: 3000 }), 40);
      return;
    }

    // 仙人掌：短促尖刺
    if (type === 'cactus') {
      playTransient({ type: 'square', freq: rand(300, 450), duration: 0.04, volume: 0.04 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(180, 240), duration: 0.06, volume: 0.03 }), 15);
      return;
    }

    // 飢餓：虛弱低鳴
    if (type === 'starve') {
      playTransient({ type: 'sine', freq: rand(80, 110), duration: 0.2, volume: 0.03 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(100, 140), duration: 0.12, volume: 0.025 }), 50);
      return;
    }

    // 怪物 / 箭矢 / 爆炸共用打擊音
    const isExplosion = type === 'creeper_explode' || type === 'bed_explode';
    const isArrow = isProjectileDamageType(type);
    const heavy = type === 'raider_brute' || type === 'enderman' || isExplosion;
    const base = isExplosion ? 108 : isArrow ? 182 : heavy ? 132 : 150;

    playNoiseBurst({
      duration: isExplosion ? 0.11 : 0.075,
      volume: isExplosion ? 0.045 : heavy ? 0.036 : 0.028,
      hp: isArrow ? 950 : 700,
      lp: isArrow ? 4200 : 2600,
      attack: 0.0008
    });
    playTransient({
      type: isArrow ? 'square' : 'sawtooth',
      freq: base + rand(-12, 14),
      duration: isExplosion ? 0.14 : 0.11,
      volume: isExplosion ? 0.065 : 0.055,
      detune: rand(-90, 90)
    });
    setTimeout(() => {
      playTransient({
        type: 'triangle',
        freq: base * rand(1.18, 1.4),
        duration: isExplosion ? 0.1 : 0.085,
        volume: isExplosion ? 0.035 : 0.028
      });
    }, 30);
  }

  function playHurt(type = 'mob') {
    if (isBurnDamageType(type)) {
      playBurnHurt(type);
      return;
    }
    playMonsterHurt(type);
  }

  function playExplosion() {
    playTransient({ type: 'square', freq: rand(62, 86), duration: 0.22, volume: 0.09 });
    setTimeout(() => playTransient({ type: 'sawtooth', freq: rand(94, 128), duration: 0.2, volume: 0.06 }), 35);
    setTimeout(() => playTransient({ type: 'triangle', freq: rand(120, 170), duration: 0.18, volume: 0.04 }), 70);
  }

  function getDistanceGain(distance = 0, far = 30, minGain = 0.2) {
    if (!Number.isFinite(distance) || distance <= 0) return 1;
    const t = Math.min(1, Math.max(0, distance / far));
    return minGain + (1 - minGain) * (1 - t);
  }

  function playMobAmbient(type = 'zombie', distance = 0) {
    const gain = getDistanceGain(distance, 34, 0.16);

    if (type === 'zombie' || type === 'husk' || type === 'drowned') {
      playTransient({ type: 'sawtooth', freq: rand(78, 112), duration: 0.2, volume: 0.045 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(62, 88), duration: 0.16, volume: 0.028 * gain }), 42);
      return;
    }

    if (type === 'skeleton' || type === 'skeleton_archer' || type === 'stray') {
      playTransient({ type: 'square', freq: rand(220, 320), duration: 0.05, volume: 0.028 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(320, 430), duration: 0.04, volume: 0.018 * gain }), 28);
      return;
    }

    if (type === 'spider') {
      playTransient({ type: 'sawtooth', freq: rand(148, 220), duration: 0.08, volume: 0.03 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(208, 280), duration: 0.07, volume: 0.02 * gain }), 26);
      return;
    }

    if (type === 'creeper') {
      playTransient({ type: 'sine', freq: rand(126, 160), duration: 0.09, volume: 0.03 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(96, 124), duration: 0.08, volume: 0.02 * gain }), 30);
      return;
    }

    if (type === 'enderman') {
      playTransient({ type: 'sine', freq: rand(170, 230), duration: 0.16, volume: 0.034 * gain, detune: rand(-900, 900) });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(84, 130), duration: 0.13, volume: 0.024 * gain, detune: rand(-700, 700) }), 58);
      return;
    }

    if (type === 'pillager' || type === 'raider_brute') {
      playTransient({ type: 'square', freq: rand(98, 136), duration: 0.11, volume: 0.04 * gain });
      setTimeout(() => playTransient({ type: 'sawtooth', freq: rand(82, 116), duration: 0.1, volume: 0.03 * gain }), 34);
      return;
    }

    if (type === 'ender_dragon') {
      playTransient({ type: 'sawtooth', freq: rand(64, 88), duration: 0.25, volume: 0.06 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(92, 128), duration: 0.2, volume: 0.04 * gain }), 70);
      return;
    }

    playTransient({ type: 'triangle', freq: rand(110, 170), duration: 0.11, volume: 0.03 * gain });
  }

  function playMobAttack(type = 'mob', mode = 'melee', distance = 0) {
    const gain = getDistanceGain(distance, 24, 0.2);
    if (mode === 'ranged') {
      playTransient({ type: 'triangle', freq: rand(240, 320), duration: 0.045, volume: 0.045 * gain });
      setTimeout(() => playTransient({ type: 'sine', freq: rand(320, 410), duration: 0.07, volume: 0.028 * gain }), 20);
      return;
    }

    if (type === 'zombie' || type === 'husk' || type === 'drowned') {
      playTransient({ type: 'sawtooth', freq: rand(88, 124), duration: 0.11, volume: 0.055 * gain });
      setTimeout(() => playTransient({ type: 'square', freq: rand(132, 176), duration: 0.08, volume: 0.03 * gain }), 28);
      return;
    }

    if (type === 'skeleton' || type === 'skeleton_archer' || type === 'stray') {
      playTransient({ type: 'square', freq: rand(250, 360), duration: 0.05, volume: 0.04 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(170, 240), duration: 0.06, volume: 0.026 * gain }), 22);
      return;
    }

    if (type === 'spider') {
      playTransient({ type: 'sawtooth', freq: rand(180, 260), duration: 0.07, volume: 0.04 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(240, 360), duration: 0.06, volume: 0.026 * gain }), 20);
      return;
    }

    if (type === 'enderman') {
      playTransient({ type: 'sine', freq: rand(220, 340), duration: 0.09, volume: 0.04 * gain, detune: rand(-1200, 1200) });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(90, 130), duration: 0.09, volume: 0.03 * gain, detune: rand(-800, 800) }), 34);
      return;
    }

    if (type === 'pillager' || type === 'raider_brute') {
      playTransient({ type: 'square', freq: rand(102, 146), duration: 0.09, volume: 0.05 * gain });
      setTimeout(() => playTransient({ type: 'sawtooth', freq: rand(146, 210), duration: 0.07, volume: 0.03 * gain }), 30);
      return;
    }

    if (type === 'ender_dragon') {
      playTransient({ type: 'sawtooth', freq: rand(68, 94), duration: 0.2, volume: 0.07 * gain });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(102, 145), duration: 0.16, volume: 0.045 * gain }), 45);
      return;
    }

    playTransient({ type: 'sawtooth', freq: rand(120, 180), duration: 0.1, volume: 0.045 * gain });
  }

  function playCreeperFuse(progress = 0, urgent = false, distance = 0) {
    const norm = Math.min(1, Math.max(0, Number(progress) || 0));
    const gain = getDistanceGain(distance, 26, 0.18);
    const baseFreq = 150 + norm * 120;
    const vol = (urgent ? 0.06 : 0.04) * gain * (0.75 + norm * 0.5);
    const dur = urgent ? 0.06 : 0.075;
    playTransient({ type: 'square', freq: baseFreq + rand(-6, 8), duration: dur, volume: vol, attack: 0.001 });
    setTimeout(() => {
      playTransient({
        type: 'triangle',
        freq: baseFreq * (urgent ? 1.42 : 1.25) + rand(-10, 10),
        duration: urgent ? 0.055 : 0.07,
        volume: vol * 0.62,
        attack: 0.001
      });
    }, urgent ? 16 : 22);
  }

  function playMobDeath(type = 'zombie', cause = 'killed') {
    if (type === 'zombie') {
      playTransient({ type: 'sawtooth', freq: rand(90, 118), duration: 0.22, volume: 0.07 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(74, 98), duration: 0.2, volume: 0.05 }), 40);
      if (cause === 'sunlight') {
        setTimeout(() => playTransient({ type: 'square', freq: rand(210, 260), duration: 0.09, volume: 0.03 }), 75);
      }
      return;
    }

    if (type === 'skeleton' || type === 'skeleton_archer') {
      playTransient({ type: 'square', freq: rand(240, 310), duration: 0.08, volume: 0.045 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(420, 520), duration: 0.06, volume: 0.03 }), 30);
      setTimeout(() => playTransient({ type: 'square', freq: rand(180, 230), duration: 0.08, volume: 0.035 }), 62);
      return;
    }

    if (type === 'creeper') {
      playTransient({ type: 'sine', freq: rand(120, 145), duration: 0.14, volume: 0.06 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(84, 110), duration: 0.18, volume: 0.05 }), 40);
      return;
    }

    playTransient({ type: 'sawtooth', freq: rand(120, 170), duration: 0.14, volume: 0.05 });
  }

  return {
    resume,
    setMasterVolume(value = 1) {
      masterVolume = clamp01(value);
    },
    getMasterVolume() {
      return masterVolume;
    },
    hitByBlockId(id) {
      const bank = (
        id === 23 || id === 71
      )
        ? digVariants.glass
        : (
          id === 5 || id === 8 || id === 9 || id === 10 || id === 13 ||
        id === 21 || id === 22 || id === 24 || id === 25 ||
        id === 28 || id === 29 || id === 30 || id === 31 ||
        id === 32 || id === 33 || id === 34 || id === 35 ||
        id === 36 || id === 37 || id === 38 || id === 39 ||
        id === 44 || id === 45 ||
        id === 40 || id === 42
      )
        ? digVariants.stone
        : (isWoodSoundBlock(id) || id === 12)
          ? digVariants.wood
          : (id === 4)
            ? digVariants.leaves
            : digVariants.grass;
      bank[Math.floor(Math.random() * bank.length)]();
    },
    break(id = null) {
      if (id === 23 || id === 71) {
        playGlassBreak();
        return;
      }
      playTransient({ type: 'square', freq: rand(85, 110), duration: 0.14, volume: 0.06 });
      playTransient({ type: 'sawtooth', freq: rand(135, 180), duration: 0.17, volume: 0.05 });
    },
    place() {
      playTransient({ type: 'triangle', freq: rand(62, 80), duration: 0.14, volume: 0.09 });
      playTransient({ type: 'sine', freq: rand(92, 120), duration: 0.08, volume: 0.05, attack: 0.001 });
    },
    placeByBlockId(id) {
      let bank = placeVariants.grass;
      if (id === 2 || id === 26 || id === 27 || id === 41) bank = placeVariants.dirt;
      else if (isWoodSoundBlock(id)) bank = placeVariants.wood;
      else if (id === 4) bank = placeVariants.leaves;
      else if (id === 12 || id === 64) bank = placeVariants.torch;
      else if (
        id === 5 || id === 8 || id === 9 || id === 10 || id === 13 ||
        id === 21 || id === 22 || id === 23 || id === 24 || id === 25 ||
        id === 28 || id === 29 || id === 30 || id === 31 ||
        id === 32 || id === 33 || id === 34 || id === 35 ||
        id === 36 || id === 37 || id === 38 || id === 39 ||
        id === 44 || id === 45 ||
        id === 40 || id === 42
      ) bank = placeVariants.stone;
      bank[Math.floor(Math.random() * bank.length)]();
    },
    pickup() {
      pickupVariants[Math.floor(Math.random() * pickupVariants.length)]();
    },
    craft() {
      playTransient({ type: 'square', freq: 420, duration: 0.08, volume: 0.035 });
      setTimeout(() => playTransient({ type: 'triangle', freq: 620, duration: 0.11, volume: 0.04 }), 80);
    },
    bowShoot() {
      playTransient({ type: 'triangle', freq: rand(240, 300), duration: 0.05, volume: 0.05 });
      setTimeout(() => playTransient({ type: 'sine', freq: rand(330, 420), duration: 0.08, volume: 0.03 }), 22);
    },
    mobHit() {
      // meaty impact: low thud + mid crunch + noise burst + high transient
      playTransient({ type: 'sine', freq: rand(65, 85), duration: 0.16, volume: 0.09 });
      playTransient({ type: 'square', freq: rand(140, 200), duration: 0.10, volume: 0.055 });
      playNoiseBurst({ duration: 0.08, volume: 0.04, hp: 200, lp: 2400 });
      setTimeout(() => playTransient({ type: 'sawtooth', freq: rand(300, 420), duration: 0.06, volume: 0.03 }), 30);
    },
    equip() {
      playTransient({ type: 'square', freq: rand(180, 230), duration: 0.08, volume: 0.04 });
      setTimeout(() => playTransient({ type: 'triangle', freq: rand(260, 320), duration: 0.07, volume: 0.03 }), 28);
    },
    eat() {
      const base = rand(280, 360);
      playTransient({ type: 'triangle', freq: base, duration: 0.06, volume: 0.045 });
      setTimeout(() => playTransient({ type: 'sine', freq: base * 0.7, duration: 0.07, volume: 0.032 }), 45);
      setTimeout(() => playTransient({ type: 'triangle', freq: base * 1.18, duration: 0.05, volume: 0.028 }), 85);
    },
    stepBySurface(surfaceId, inWater = false, sprinting = false) {
      playStepBySurface(surfaceId, inWater, sprinting);
    },
    hurt(type = 'mob') {
      playHurt(type);
    },
    mobAmbient(type = 'zombie', distance = 0) {
      playMobAmbient(type, distance);
    },
    mobAttack(type = 'mob', mode = 'melee', distance = 0) {
      playMobAttack(type, mode, distance);
    },
    creeperFuse(progress = 0, urgent = false, distance = 0) {
      playCreeperFuse(progress, urgent, distance);
    },
    explosion() {
      playExplosion();
    },
    mobDeath(type = 'zombie', cause = 'killed') {
      playMobDeath(type, cause);
    },
    sizzle() {
      // 低沉灼燒底噪 — 像肉碰到滾燙鐵板
      playNoiseBurst({ duration: 0.28, volume: 0.055, hp: 600, lp: 3200, attack: 0.001 });
      // 粗糙嘶聲
      playTransient({ type: 'sawtooth', freq: rand(280, 420), duration: 0.22, volume: 0.045, detune: rand(-300, 300) });
      // 悶痛低頻
      setTimeout(() => playTransient({ type: 'square', freq: rand(80, 120), duration: 0.18, volume: 0.04 }), 40);
      // 蒸氣尾音
      setTimeout(() => playNoiseBurst({ duration: 0.15, volume: 0.03, hp: 1200, lp: 4800, attack: 0.002 }), 120);
    }
  };
}
